<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  .upload-item {
    background-color: colors.get(interfaceSwGrey_4);
    border: 1px solid colors.get(interfaceSwGrey_10);
    border-radius: 8px;
    height: 58px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 8px 8px 4px;
    position: relative;
  }

  .upload-item-left {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
  }

  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 8px;

    .avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: colors.get(primary_40);
      width: 24px;
      height: 24px;
      border-radius: 20px;

      &.processing {
        background-color: colors.get(interfaceSwGrey_20);
      }

      &.done {
        background-color: colors.get(primary_40);
      }
    }
  }

  .upload-name {
    @include mixins.body3;
    color: colors.get(textBlack);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .upload-info {
    min-width: 0;
    flex: 1;
  }

  .upload-subtitle {
    @include mixins.body3;
    color: colors.get(textDisabledGrey);
    white-space: nowrap;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 8px;

    .upload-candidate-name {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .subtitle-divider {
    width: 1px;
    height: 21px;
    background-color: colors.get(interfaceSwGrey_10);
  }

  .view-button {
    background-color: white;
    border: 1.4px solid colors.get(interfaceSwGrey_10);
    border-radius: 8px;
    height: 32px;
    padding: 0 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    color: colors.get(primary_50);

    &:hover {
      background-color: colors.get(interfaceSwGrey_4);
    }

    .view-text {
      @include mixins.button3;
      white-space: nowrap;
    }
  }

  .dismiss-button {
    background: none;
    border: none;
    padding: 4px 8px;
    cursor: pointer;
    color: colors.get(textGrey);

    &:hover {
      color: colors.get(textBlack);
    }
  }

  .upload-item-right {
    display: flex;
    align-items: center;
    gap: 0px;
  }

  .progress-bar-container {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background-color: colors.get(interfaceSwGrey_10);
    border-radius: 0 0 8px 8px;
    overflow: hidden;
  }

  .progress-bar {
    height: 100%;
    width: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      colors.get(primary_50),
      transparent
    );
    animation: slide 2s ease-in-out infinite;
  }

  @keyframes slide {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
</style>

<script>
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import Icon from "@sourcewhale/shared/svelte-design-system/components/atoms/Icon.svelte";
  import { onMount } from "svelte";
  import { updateUploadsNotifications } from "../../../../api/uploads-notifications";
  import { todosModalItemStore } from "../../../modals/TodosModal/stores";
  import { uploadsModalStore, uploadsNotificationsStore } from "./stores";

  export let upload;

  let modalExists = false;

  onMount(() => {
    modalExists = document.getElementById("todos-modal");
  });

  const handleDismiss = async (upload) => {
    updateUploadsNotifications({
      id: upload["notificationId"],
      status: "deleted",
    });
    uploadsNotificationsStore.update((store) => ({
      notifications: store.notifications.filter(
        (n) => n.notificationId !== upload.notificationId
      ),
    }));
  };

  const handleView = (upload) => {
    const candidateId = upload?.candidates[0]?.candidateId;
    if (!candidateId) {
      return;
    }
    if (!modalExists || window.location.pathname == "/todo") {
      window.location = "/contacts?openContactModalWithId=" + candidateId;
      return;
    }

    todosModalItemStore.set({
      candidateId: candidateId,
      toDoId: null,
    });
    uploadsModalStore.set(false);
  };

  const formatDate = (date) => {
    const options = { day: "numeric", month: "short" };
    return new Date(date * 1000).toLocaleDateString("en-GB", options);
  };

  const STATUS_TO_TEXT = {
    done: "Creating contact...",
    processing: "Creating contact...",
    error: "Failed to create contact",
  };
</script>

<div class="upload-item">
  {#if upload.status === "processing"}
    <div class="progress-bar-container">
      <div class="progress-bar"></div>
    </div>
  {/if}

  <div class="upload-item-left">
    <div class="icon-container">
      {#if upload.status === "processing" || upload.status === "done"}
        <div class="avatar {upload.status}">
          <Icon class="fa-regular fa-user color-interfaceWhite" size="xs" />
        </div>
      {:else}
        <Icon class="fa-regular fa-exclamation-circle color-systemError_100"
        ></Icon>
      {/if}
    </div>
    <div class="upload-info">
      <div class="upload-name">{upload.filename}</div>
      <div class="upload-subtitle">
        <span class="upload-candidate-name"
          >{upload?.candidates?.[0]?.name ||
            STATUS_TO_TEXT[upload.status]}</span
        >
        {#if upload.status === "done" || upload.status === "error"}
          <div class="subtitle-divider"></div>
          <span>Created {formatDate(upload.updatedAt)}</span>
        {/if}
      </div>
    </div>
  </div>

  <div class="upload-item-right">
    {#if upload.status === "done"}
      <Button
        class="view-button"
        onclick={() => handleView(upload)}
        kind="tertiary"
        size="xsmall"
        trailingIcon="fa-arrow-up-right"
      >
        View
      </Button>
    {/if}

    {#if upload.status === "done" || upload.status === "error"}
      <Button
        kind="ghost-grey"
        class="dismiss-button"
        onclick={() => handleDismiss(upload)}
      >
        <Icon class="fa-regular fa-xmark" size="small" />
      </Button>
    {/if}
  </div>
</div>
